import styled from 'styled-components';
import type { DefaultTheme } from 'styled-components';
import type { CaseStatus } from '@/core/models/case';
import { STATUS_LABEL, STATUS_TONE, type StatusTone } from '../meta';

/**
 * Case-status colour, from the theme rather than from four raw hexes.
 *
 * These were literals (#ffb13b, #36e0a0, #a13bff, #2fd9e0) that no token knew
 * about, two of them neon, on the one screen the whole platform is an argument
 * for. Now:
 *   in progress  -> the reserved `review` ochre, which no accent preset can
 *                   repaint, so the public record does not change colour when a
 *                   member picks a different accent;
 *   concluded    -> `success`, the same green a decided case already reads as.
 *
 * A case awaiting a head and a case under review share a colour on purpose,
 * for the same reason PostStatus renders them identically: which internal step
 * the Council is on is not the member's business, and splitting them would
 * imply something is being withheld.
 */
const TONE: Record<StatusTone, (t: DefaultTheme) => string> = {
  amber: (t) => t.color.review,
  violet: (t) => t.color.review,
  green: (t) => t.color.success,
  cyan: (t) => t.color.success,
};

const Pill = styled.span<{ $tone: StatusTone }>`
  display: inline-block;
  padding: 2px 8px;
  /* State is carried by the tone and the word; the shape is the app's small tag. */
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 600;
  color: ${({ $tone, theme }) => TONE[$tone](theme)};
  background: ${({ $tone, theme }) =>
    `color-mix(in srgb, ${TONE[$tone](theme)} 14%, transparent)`};
`;

export function StatusPill({ status }: { status: CaseStatus }) {
  return <Pill $tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</Pill>;
}
